import { asRecord } from 'shared/utils/as-record';
import { generateId } from 'shared/utils/entity-id';
import type { z } from 'zod';
import { useUserStore } from '~/modules/user/user-store';

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** A zod node exposes its definition tag; anything else is not a schema this walker can read. */
const isSchema = (value: unknown): value is z.ZodType => isRecord(value) && isRecord(value.def) && typeof value.def.type === 'string';

const asSchema = (value: unknown): z.ZodType | undefined => (isSchema(value) ? value : undefined);

const asSchemaList = (value: unknown): z.ZodType[] => (Array.isArray(value) ? value.filter(isSchema) : []);

const asShape = (value: unknown): Record<string, z.ZodType> => {
  if (!isRecord(value)) return {};
  const shape: Record<string, z.ZodType> = {};
  for (const [key, field] of Object.entries(value)) {
    if (isSchema(field)) shape[key] = field;
  }
  return shape;
};

/** Per-variant payload fields (`shape`, `options`, `entries`, ...) are not on the shared base type, so each is read as `unknown` and narrowed at its use. */
const defField = (def: z.ZodType['def'], key: string): unknown => asRecord(def)[key];

const getDefaultForZodType = (schema: z.ZodType): unknown => {
  const def = schema.def;

  switch (def.type) {
    case 'string':
      return '';

    case 'number':
    case 'bigint':
      return 0;

    case 'boolean':
      return false;

    case 'date':
      return new Date().toISOString();

    case 'null':
      return null;

    case 'undefined':
      return undefined;

    case 'array':
      return [];

    case 'object': {
      const shape = asShape(defField(def, 'shape'));
      const result: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(shape)) {
        result[key] = getDefaultForZodType(value);
      }
      return result;
    }

    case 'enum': {
      const entries = defField(def, 'entries');
      return isRecord(entries) ? Object.values(entries)[0] : '';
    }

    case 'literal': {
      // Zod v4 stores every accepted literal in `values`; the first is the canonical default.
      const values = defField(def, 'values');
      return Array.isArray(values) ? values[0] : undefined;
    }

    case 'union': {
      // Null wins over the first option, since z.union([z.string(), z.null()]) should default to null.
      const options = asSchemaList(defField(def, 'options'));
      for (const option of options) {
        if (option.def.type === 'null') return null;
      }
      const [first] = options;
      return first ? getDefaultForZodType(first) : null;
    }

    case 'optional': {
      const innerType = asSchema(defField(def, 'innerType'));
      return innerType ? getDefaultForZodType(innerType) : undefined;
    }

    case 'nullable':
      return null;

    case 'default': {
      const defaultValue = defField(def, 'defaultValue');
      return typeof defaultValue === 'function' ? defaultValue() : defaultValue;
    }

    case 'record':
      return {};

    case 'map':
      return new Map();

    case 'set':
      return new Set();

    case 'tuple':
      return asSchemaList(defField(def, 'items')).map(getDefaultForZodType);

    case 'any':
    case 'unknown':
      return null;

    default:
      console.debug(`[createOptimisticEntity] Unhandled Zod type: ${def.type}`);
      return null;
  }
};

export const getSchemaDefaults = <T extends z.ZodObject<z.ZodRawShape>>(schema: T): z.infer<T> => {
  const result: Record<string, unknown> = {};

  // A generic `ZodRawShape` types its fields as core nodes, so they take the same structural check the walker uses for nested shapes.
  for (const [key, value] of Object.entries(asShape(schema.def.shape))) {
    result[key] = getDefaultForZodType(value);
  }

  return result as z.infer<T>;
};

/** Fields the optimistic builder always supplies, so callers may not pass them. */
type AutoGeneratedFields = 'id' | 'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy';

/** Schema defaults, then caller input, then generated identity, timestamps, and user references. */
export const createOptimisticEntity = <T extends z.ZodObject<z.ZodRawShape>>(
  schema: T,
  input: Partial<Omit<z.infer<T>, AutoGeneratedFields>> & { id?: string } = {},
): z.infer<T> => {
  const user = useUserStore.getState().user;
  const createdByUser = user
    ? {
        id: user.id,
        name: user.name,
        slug: user.slug,
        thumbnailUrl: user.thumbnailUrl,
        email: user.email,
        entityType: 'user' as const,
      }
    : null;

  const defaults = getSchemaDefaults(schema);

  return {
    ...defaults,
    ...input,
    id: input.id || generateId(),
    createdAt: new Date().toISOString(),
    createdBy: createdByUser,
    updatedAt: null,
    updatedBy: null,
    _optimistic: true,
  } as z.infer<T>;
};
